import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import {
    IconChevronRight, IconUsers, IconSend, IconCircleCheck,
    IconStarFilled, IconAward, IconMessage2, IconTrendingUp,
    IconChartBar, IconEye, IconPencil, IconCirclePlus,
    IconTarget, IconSchool, IconFlag, IconUserCircle, IconCheck
} from '@tabler/icons-react';

export default function SelfAppraisal() {
    const [currentStep, setCurrentStep] = useState('self-appraisal');

    const steps = [
        { id: 'self-appraisal', label: 'Self Appraisal', icon: IconPencil },
        { id: 'manager-review', label: 'Manager Review', icon: IconMessage2 },
        { id: 'growth-plan', label: 'Growth Plan', icon: IconTrendingUp },
        { id: 'review', label: 'Review & Submit', icon: IconCircleCheck }
    ];

    const [kras, setKras] = useState([
        { id: 1, title: 'Revenue Growth', target: '15% YoY', achieved: '18%', rating: 4, comments: 'Exceeded revenue targets by focusing on high-value clients.', managerRating: 4, managerComments: '' },
        { id: 2, title: 'Team Development', target: '5 team members trained', achieved: '6 members', rating: 5, comments: 'Successfully mentored 6 team members; 2 have already been promoted.', managerRating: 4, managerComments: '' },
        { id: 3, title: 'Process Improvement', target: 'Reduce TAT by 20%', achieved: '25% reduction', rating: 4, comments: 'Implemented new automation tools that significantly reduced manual entry.', managerRating: 4, managerComments: '' },
        { id: 4, title: 'Customer Satisfaction', target: 'NPS > 70', achieved: 'NPS 75', rating: 4, comments: 'Maintained high customer satisfaction through proactive communication.', managerRating: 4, managerComments: '' },
        { id: 5, title: 'Quality of Output', target: 'Error rate < 1%', achieved: '0.8%', rating: 4, comments: 'Maintained high accuracy in all deliverables and reporting.', managerRating: 4, managerComments: '' },
        { id: 6, title: 'Timeliness and Productivity', target: '100% On-time delivery', achieved: '98%', rating: 4, comments: 'Consistently met project deadlines with high task throughput.', managerRating: 4, managerComments: '' }
    ]);

    const renderStep = () => {
        switch (currentStep) {
            case 'self-appraisal': return <SelfAppraisalForm kras={kras} setKras={setKras} setCurrentStep={setCurrentStep} />;
            case 'manager-review': return <ManagerReview kras={kras} setKras={setKras} setCurrentStep={setCurrentStep} />;
            case 'growth-plan': return <GrowthPlan setCurrentStep={setCurrentStep} />;
            case 'review': return <ReviewSubmit setCurrentStep={setCurrentStep} />;
            default: return <SelfAppraisalForm kras={kras} setKras={setKras} setCurrentStep={setCurrentStep} />;
        }
    };

    return (
        <div className="pb-30">
            <div className="flex justify-between items-center mb-30">
                <Breadcrumb title="Appraisal Workflow" path="Performance / Review Cycle" />
                <div className="flex items-center gap-10">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-text-secondary bg-bg-dark border border-border px-12 py-6 rounded-lg shadow-sm">
                        Status: <span className="text-warning ml-5">In Progress</span>
                    </span>
                </div>
            </div>

            {/* Progress Stepper */}
            <div className="flex gap-20 overflow-x-auto pb-15 scrollbar-hide mb-30">
                {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = step.id === currentStep;
                    const isCompleted = steps.findIndex(s => s.id === currentStep) > index;

                    return (
                        <div
                            key={step.id}
                            onClick={() => setCurrentStep(step.id)}
                            className={`group flex-1 min-w-[160px] p-20 rounded-lg border transition-all cursor-pointer shadow-light
                ${isActive
                                    ? 'bg-bg-dark border-primary shadow-medium ring-1 ring-primary/20'
                                    : isCompleted
                                        ? 'bg-bg-dark border-success/30 opacity-70'
                                        : 'bg-bg-dark border-border hover:border-primary/50'}`}
                        >
                            <div className="flex items-center justify-between mb-15">
                                <div className={`p-10 rounded-lg ${isActive ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-bg-light text-text-secondary'} transition-all`}>
                                    <Icon size={18} />
                                </div>
                                {isCompleted && <div className="w-20 h-20 rounded-full bg-success flex items-center justify-center"><IconCircleCheck size={14} className="text-white" /></div>}
                            </div>
                            <div>
                                <div className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-primary' : 'text-text-secondary mb-2'}`}>Step {index + 1}</div>
                                <div className={`text-sm font-medium ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>{step.label}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="transition-all duration-300">
                {renderStep()}
            </div>
        </div>
    );
}


function SelfAppraisalForm({ kras, setKras, setCurrentStep }) {

    return (
        <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
            <div className="p-20 border-b border-border bg-bg-light-100/30 flex justify-between items-center">
                <div>
                    <h2 className="text-[16px] font-medium text-text-primary uppercase tracking-widest">Self Appraisal Details</h2>
                    <p className="text-text-secondary text-[10px] mt-2 font-bold uppercase tracking-widest opacity-70">Impact assessment for current cycle</p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">State: <span className="text-warning">Draft</span></p>
                    <p className="text-xs font-medium text-text-primary mt-1">Today, 2:45 PM</p>
                </div>
            </div>

            <div className="p-20 space-y-20">
                {kras.map((kra, index) => (
                    <div key={kra.id} className="p-20 rounded-lg border border-border bg-bg-light/20 hover:border-primary/30 transition-all shadow-sm">
                        <div className="flex justify-between items-start mb-20">
                            <div className="flex gap-15">
                                <div className="w-10 h-10 rounded-full bg-primary mt-6 shadow-glow shadow-primary/20"></div>
                                <div>
                                    <h3 className="text-base font-bold text-text-primary mb-5 tracking-tight">{kra.title}</h3>
                                    <div className="flex gap-15 text-[11px] font-bold text-text-secondary uppercase tracking-widest">
                                        <span className="flex items-center gap-5">Target: <span className="text-warning">{kra.target}</span></span>
                                        <span className="w-px h-10 bg-border mt-3"></span>
                                        <span className="flex items-center gap-5">Achieved: <span className="text-success">{kra.achieved}</span></span>
                                    </div>
                                </div>
                            </div>
                            <div className="px-10 py-4 rounded border border-primary/20 bg-primary/5 text-primary text-[10px] font-bold uppercase tracking-widest">
                                KPI Block 0{index + 1}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-25">
                            <div className="lg:col-span-1">
                                <label className="text-[10px] font-extrabold text-text-secondary uppercase mb-10 block tracking-widest">Self Rating</label>
                                <div className="flex gap-5 items-center">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button key={star} onClick={() => {
                                            const newKras = [...kras];
                                            newKras[index].rating = star;
                                            setKras(newKras);
                                        }} className="transition-transform hover:scale-110">
                                            <IconStarFilled size={22} className={star <= kra.rating ? 'text-warning drop-shadow-sm' : 'text-text-secondary opacity-20'} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="lg:col-span-3">
                                <label className="text-[10px] font-extrabold text-text-secondary uppercase mb-10 block tracking-widest">Achievements & Proof</label>
                                <textarea
                                    className="w-full bg-bg-dark border border-border rounded-lg p-15 text-sm text-text-primary focus:border-primary outline-none transition-all placeholder:text-text-secondary/50 resize-none h-[110px] shadow-inner"
                                    value={kra.comments}
                                    placeholder="Describe your achievements and key results in detail..."
                                    onChange={(e) => {
                                        const newKras = [...kras];
                                        newKras[index].comments = e.target.value;
                                        setKras(newKras);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="p-25 border-t border-border bg-bg-light-100/10 flex gap-20">
                <button onClick={() => window.location.href = '/performance/kpi'} className="btn btn-outline-secondary">Save Draft & Exit</button>
                <button onClick={() => setCurrentStep('manager-review')} className="ml-auto btn btn-primary flex items-center gap-10 shadow-lg shadow-primary/20">
                    Continue to Manager Review <IconChevronRight size={18} />
                </button>
            </div>
        </div>
    );
}

function ManagerReview({ kras, setKras, setCurrentStep }) {
    return (
        <div className="space-y-30">
            {/* KRA Specific Managerial Review */}
            <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                <div className="p-20 border-b border-border bg-bg-light-100/30 flex justify-between items-center">
                    <div>
                        <h2 className="text-[16px] font-medium text-text-primary uppercase tracking-widest">KRA Managerial Validation</h2>
                        <p className="text-text-secondary text-[10px] mt-2 font-bold uppercase tracking-widest opacity-70">Reviewing supervisee evidence and assigning final scores</p>
                    </div>
                </div>

                <div className="p-20 space-y-20">
                    {kras.map((kra, index) => (
                        <div key={kra.id} className="p-20 rounded-lg border border-border bg-bg-light/10 hover:border-primary/20 transition-all">
                            <div className="flex justify-between items-center mb-15">
                                <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">{kra.title}</h3>
                                <div className={`text-[9px] font-black uppercase px-10 py-4 rounded-full border ${kra.rating !== kra.managerRating ? 'bg-warning/10 border-warning/30 text-warning' : 'bg-success/10 border-success/30 text-success'}`}>
                                    {kra.rating !== kra.managerRating ? 'Rating Calibration Needed' : 'In Agreement'}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-30">
                                {/* Left: Supervisee Perspective (Read Only) */}
                                <div className="p-15 rounded-lg bg-bg-dark border border-border/50">
                                    <div className="flex justify-between items-center mb-10">
                                        <span className="text-[10px] font-black text-text-secondary uppercase tracking-widest opacity-60">Supervisee Perspective</span>
                                        <div className="flex gap-2">
                                            {[1, 2, 3, 4, 5].map(s => (
                                                <IconStarFilled key={s} size={14} className={s <= kra.rating ? 'text-warning' : 'text-text-secondary opacity-10'} />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-xs text-text-primary italic opacity-70 leading-relaxed font-serif">"{kra.comments}"</p>
                                </div>

                                {/* Right: Manager Perspective (Editable) */}
                                <div className="space-y-15">
                                    <div>
                                        <label className="text-[10px] font-black text-primary uppercase mb-8 block tracking-widest">Managerial Rating</label>
                                        <div className="flex gap-8">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button key={star} onClick={() => {
                                                    const newKras = [...kras];
                                                    newKras[index].managerRating = star;
                                                    setKras(newKras);
                                                }} className="transition-transform hover:scale-110">
                                                    <IconStarFilled size={20} className={star <= kra.managerRating ? 'text-primary' : 'text-text-secondary opacity-20'} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-text-secondary uppercase mb-8 block tracking-widest">Manager Feedback / Observations</label>
                                        <textarea
                                            className="w-full bg-bg-dark border border-border rounded-lg p-12 text-xs text-text-primary focus:border-primary outline-none transition-all h-[60px] resize-none"
                                            placeholder="Add specific feedback for this KRA..."
                                            value={kra.managerComments}
                                            onChange={(e) => {
                                                const newKras = [...kras];
                                                newKras[index].managerComments = e.target.value;
                                                setKras(newKras);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Overall Narrative Feedback */}
            <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                <div className="p-20 border-b border-border bg-bg-light-100/30 flex justify-between items-center">
                    <div>
                        <h2 className="text-[16px] font-medium text-text-primary uppercase tracking-widest">Narrative Feedback</h2>
                        <p className="text-text-secondary text-[10px] mt-2 font-bold uppercase tracking-widest opacity-70">Managerial Assessment & Qualitative Insight</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">Manager: <span className="text-primary text-[11px]">Sarah Jenkins</span></p>
                    </div>
                </div>

                <div className="p-25 space-y-30">
                    <div className="grid grid-cols-1 gap-25">
                        <div>
                            <label className="text-[10px] font-extrabold text-success uppercase mb-12 block tracking-widest border-l-2 border-success pl-10">Strengths & Achievements</label>
                            <textarea
                                className="w-full bg-bg-light/10 border border-border rounded-xl p-20 text-[13px] text-text-primary focus:border-success/50 outline-none transition-all placeholder:text-text-secondary/30 resize-none h-[120px] shadow-inner font-medium leading-relaxed"
                                placeholder="Detail the employee's key successes and qualitative wins..."
                                defaultValue="Oyindamola has shown exceptional attention to detail in the NDR accuracy audits. Her ability to spot inconsistencies before they hit the global reporting stage has saved the team significant time."
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-extrabold text-warning uppercase mb-12 block tracking-widest border-l-2 border-warning pl-10">Areas for Growth</label>
                            <textarea
                                className="w-full bg-bg-light/10 border border-border rounded-xl p-20 text-[13px] text-text-primary focus:border-warning/50 outline-none transition-all placeholder:text-text-secondary/30 resize-none h-[120px] shadow-inner font-medium leading-relaxed"
                                placeholder="Provide constructive advice on lower-scoring areas..."
                                defaultValue="While technical skills are top-tier, I'd like to see more proactive 'Strategic Leadership' in departmental meetings. Moving from execution to leading the session would be a great next step."
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-extrabold text-primary uppercase mb-12 block tracking-widest border-l-2 border-primary pl-10">Development Plan</label>
                            <textarea
                                className="w-full bg-bg-light/10 border border-border rounded-xl p-20 text-[13px] text-text-primary focus:border-primary/50 outline-none transition-all placeholder:text-text-secondary/30 resize-none h-[120px] shadow-inner font-medium leading-relaxed"
                                placeholder="Agreed future training and milestones..."
                                defaultValue="We've agreed on enrolling you in the 'Power BI Advanced Dashboarding' and 'DHIS2 Configuration' training for Q2 to support our automation goals."
                            />
                        </div>
                    </div>
                </div>

                <div className="p-25 border-t border-border bg-bg-light-100/10 flex gap-15">
                    <button onClick={() => setCurrentStep('self-appraisal')} className="btn btn-outline-secondary">Back to Self Appraisal</button>
                    <button onClick={() => setCurrentStep('growth-plan')} className="ml-auto btn btn-primary flex items-center gap-10 shadow-lg shadow-primary/20">
                        Continue to Growth Plan <IconChevronRight size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function GrowthPlan({ setCurrentStep }) {
    return (
        <div className="space-y-30">
            {/* Main Growth Roadmap */}
            <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                <div className="p-20 border-b border-border bg-bg-light-100/30 flex justify-between items-center">
                    <div>
                        <h2 className="text-[16px] font-medium text-text-primary uppercase tracking-widest">Strategic Growth Roadmap</h2>
                        <p className="text-text-secondary text-[10px] mt-2 font-bold uppercase tracking-widest opacity-70">Defined objectives for the 2026 Fiscal Year</p>
                    </div>
                    <div className="flex gap-10">
                        <button className="flex items-center gap-8 px-12 py-6 rounded-lg bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest hover:bg-primary/20 transition-all">
                            <IconCirclePlus size={14} /> Add Objective
                        </button>
                    </div>
                </div>

                <div className="p-25">
                    <div className="grid grid-cols-12 gap-30">
                        {/* Left Column: Primary Goals & Actions */}
                        <div className="col-span-12 lg:col-span-8 space-y-25">
                            <div>
                                <label className="text-[10px] font-black text-text-secondary uppercase mb-15 block tracking-widest opacity-60 flex items-center gap-8">
                                    <IconTarget size={14} /> Core Development Goal
                                </label>
                                <div className="p-20 rounded-xl bg-bg-light/10 border border-border border-l-4 border-l-primary group hover:bg-bg-light/20 transition-all">
                                    <h4 className="text-lg font-bold text-text-primary mb-10 group-hover:text-primary transition-colors">Cross-Functional Leadership & Analytics Mastery</h4>
                                    <p className="text-sm text-text-secondary leading-relaxed font-medium">Transition from a technical execution role to a strategic lead by spearheading inter-departmental data audits and completing advanced predictive modeling certifications.</p>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-text-secondary uppercase mb-15 block tracking-widest opacity-60 flex items-center gap-8">
                                    <IconFlag size={14} /> Actionable Milestones
                                </label>
                                <div className="space-y-12">
                                    {[
                                        { task: 'Complete Advanced DHIS2 Configuration Course', date: 'March 2026', status: 'In Progress', color: 'text-warning' },
                                        { task: 'Lead the Q2 NDR Global Data Quality Audit', date: 'June 2026', status: 'Pending', color: 'text-text-secondary' },
                                        { task: 'Establish a monthly Peer-to-Peer Knowledge Share', date: 'Ongoing', status: 'Active', color: 'text-success' }
                                    ].map((m, i) => (
                                        <div key={i} className="flex items-center justify-between p-15 rounded-xl bg-bg-light/5 border border-border group hover:border-primary/30 transition-all">
                                            <div className="flex items-center gap-15">
                                                <div className="w-24 h-24 rounded-full border-2 border-border group-hover:border-primary/50 flex items-center justify-center transition-all">
                                                    {m.status === 'Active' && <IconCheck size={14} className="text-success" />}
                                                </div>
                                                <span className="text-sm font-medium text-text-primary">{m.task}</span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-text-primary tracking-tighter uppercase">{m.date}</p>
                                                <p className={`text-[9px] font-black uppercase tracking-widest ${m.color}`}>{m.status}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Support & Resources */}
                        <div className="col-span-12 lg:col-span-4 space-y-25">
                            <div>
                                <label className="text-[10px] font-black text-text-secondary uppercase mb-15 block tracking-widest opacity-60 flex items-center gap-8">
                                    <IconUserCircle size={14} /> Support Network
                                </label>
                                <div className="p-20 rounded-xl bg-bg-light/10 border border-border space-y-15">
                                    <div className="flex items-center gap-12 pb-15 border-b border-border/50">
                                        <div className="w-35 h-35 rounded-full bg-primary/20 border border-primary/30" />
                                        <div>
                                            <p className="text-xs font-bold text-text-primary leading-none mb-4">Sarah Jenkins</p>
                                            <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest">Primary Mentor</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-12">
                                        <div className="w-35 h-35 rounded-full bg-success/20 border border-success/30" />
                                        <div>
                                            <p className="text-xs font-bold text-text-primary leading-none mb-4">Marcus Tunde</p>
                                            <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest">Peer Buddy (Cross-Dept)</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-text-secondary uppercase mb-15 block tracking-widest opacity-60 flex items-center gap-8">
                                    <IconSchool size={14} /> Recommended Learning
                                </label>
                                <div className="space-y-10 text-[11px] font-bold text-text-secondary uppercase tracking-tighter">
                                    {['Strategic Influence for Managers', 'DQA Automation Workflows', 'Python for Global Health'].map(course => (
                                        <div key={course} className="flex items-center gap-10 p-10 rounded-lg border border-border bg-bg-light/10 hover:text-primary hover:border-primary/30 cursor-pointer transition-all">
                                            <div className="w-6 h-6 rounded-full bg-primary/40" />
                                            {course}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="p-20 rounded-xl bg-primary/5 border border-primary/10">
                                <h3 className="text-[10px] font-black text-primary uppercase mb-10 tracking-[0.2em]">Execution Timeline</h3>
                                <div className="flex justify-between items-end mb-8">
                                    <span className="text-[9px] font-black text-text-secondary uppercase">Progress: Phase 1</span>
                                    <span className="text-[11px] font-bold text-primary">25%</span>
                                </div>
                                <div className="w-full bg-bg-dark h-[6px] rounded-full overflow-hidden border border-border/50 shadow-inner">
                                    <div className="bg-primary h-full w-[25%] transition-all relative">
                                        <div className="absolute inset-0 bg-white/20 animate-pulse" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-25 border-t border-border bg-bg-light-100/10 flex gap-15">
                    <button onClick={() => setCurrentStep('manager-review')} className="btn btn-outline-secondary">Previous Step</button>
                    <button onClick={() => setCurrentStep('review')} className="ml-auto btn btn-primary shadow-lg shadow-primary/20">Proceed to Final Review</button>
                </div>
            </div>
        </div>
    );
}

function ReviewSubmit({ setCurrentStep }) {
    const [signedOff, setSignedOff] = useState(false);

    return (
        <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
            <div className="p-60 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 p-15 opacity-5 pointer-events-none transition-opacity group-hover:opacity-10">
                    <IconAward size={120} />
                </div>
                <div className="w-80 h-80 rounded-2xl bg-success/10 mx-auto flex items-center justify-center text-success mb-25 border border-success/20 shadow-lg shadow-success/10">
                    <IconAward size={40} />
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-12 tracking-tight">Cycle Assessment Complete</h2>
                <p className="text-text-secondary text-[13px] mb-40 max-w-sm mx-auto leading-relaxed font-medium">Excellent work! You've successfully documented your impact and outlined your future growth. Please sign off below to confirm you have discussed this with your manager.</p>

                <div className="max-w-xs mx-auto mb-20">
                    <label
                        onClick={() => setSignedOff(!signedOff)}
                        className={`group flex items-center gap-15 p-15 rounded-xl border-2 transition-all cursor-pointer shadow-sm
                            ${signedOff ? 'bg-success/5 border-success shadow-success/10' : 'bg-bg-light/20 border-border hover:border-primary/30'}`}
                    >
                        <div className={`w-24 h-24 rounded-lg border-2 flex items-center justify-center transition-all 
                            ${signedOff ? 'bg-success border-success' : 'border-border group-hover:border-primary/50'}`}>
                            {signedOff && <IconCircleCheck size={16} className="text-white" />}
                        </div>
                        <span className={`text-[11px] font-bold uppercase tracking-widest ${signedOff ? 'text-success' : 'text-text-secondary'}`}>
                            Digital Sign-off / Accept
                        </span>
                    </label>
                </div>
            </div>
            <div className="p-25 border-t border-border bg-bg-light-100/10 flex gap-15 justify-center">
                <button onClick={() => setCurrentStep('growth-plan')} className="btn btn-outline-secondary">Review Summary</button>
                <button
                    disabled={!signedOff}
                    className={`btn px-40 shadow-lg transition-all ${signedOff ? 'btn-primary shadow-primary/20' : 'btn-secondary opacity-50 cursor-not-allowed'}`}
                >Submit Final Appraisal
                </button>
            </div>
        </div>
    );
}